// The option tables the criteria builder's selects are filled from
// (`search.page.component.ng1.ts:178-230`).
//
// ⚠️ EVERY ONE OF THESE ENUMS IS NUMERIC, and the numbers are what goes on the wire.
// `enum LastUsedPeriod { ANY_TIME, CURRENTLY, RECENTLY, IN_THE_PAST }` has no explicit values,
// so TypeScript assigns 0-3 and the saved criteria carry `when: 2`, not `when: "RECENTLY"`.
// Porting these as string unions — which reads better and is what a fresh design would do —
// produces criteria the server stores and then cannot match against.

export const LastUsedPeriod = {
  AnyTime: 0,
  Currently: 1,
  Recently: 2,
  InThePast: 3,
} as const
export type LastUsedPeriod = (typeof LastUsedPeriod)[keyof typeof LastUsedPeriod]

export const LogicalOperator = {
  All: 0,
  Any: 1,
} as const
export type LogicalOperator = (typeof LogicalOperator)[keyof typeof LogicalOperator]

export const CriteriaConditionType = {
  IncludeAll: 0,
  IncludeAny: 1,
  /** Declared upstream and deliberately NOT offered — see `KEYWORD_CONDITION_OPTIONS`. */
  Optional: 2,
  Exclude: 3,
} as const
export type CriteriaConditionType = (typeof CriteriaConditionType)[keyof typeof CriteriaConditionType]

export interface Option<T> {
  display: string
  value: T
}

/**
 * ⚠️ THE FIRST ENTRY IS A PLACEHOLDER WITH A `null` VALUE, not an unset option list. "Min" and
 * "Max" are selectable rows that clear the bound, so dropping them for a `placeholder`
 * attribute would leave a user who set a minimum unable to unset it.
 */
export const MIN_YEARS_OPTIONS: ReadonlyArray<Option<number | null>> = [
  { display: 'Min', value: null },
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ display: String(n), value: n })),
]

export const MAX_YEARS_OPTIONS: ReadonlyArray<Option<number | null>> = [
  { display: 'Max', value: null },
  ...[1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({ display: String(n), value: n })),
]

/** `lastUsedDisplayText` — the "When" column. */
export const LAST_USED_OPTIONS: ReadonlyArray<Option<LastUsedPeriod>> = [
  { display: 'Any time', value: LastUsedPeriod.AnyTime },
  { display: 'Currently', value: LastUsedPeriod.Currently },
  // The label says three years and the enum member is called RECENTLY; both are upstream's.
  { display: 'Last 3 years', value: LastUsedPeriod.Recently },
  { display: 'In the past', value: LastUsedPeriod.InThePast },
]

/** `logicalOperatorText` — how multiple criteria combine. */
export const LOGICAL_OPERATOR_OPTIONS: ReadonlyArray<Option<LogicalOperator>> = [
  { display: 'Match all', value: LogicalOperator.All },
  { display: 'Match one or more', value: LogicalOperator.Any },
]

/** `logicalOperatorClauseText` — the word printed BETWEEN two criteria rows. */
export const LOGICAL_OPERATOR_CLAUSE: Record<LogicalOperator, string> = {
  [LogicalOperator.All]: 'AND',
  [LogicalOperator.Any]: 'OR',
}

/**
 * `keywordConditionTypeDisplayText`.
 *
 * ⚠️ THREE OF THE FOUR. `OPTIONAL` exists in the enum and is commented out of this list
 * upstream — "Does not appear in PRD" — so offering it would add a condition the product
 * deliberately does not expose, while still being a value the server understands.
 */
export const KEYWORD_CONDITION_OPTIONS: ReadonlyArray<Option<CriteriaConditionType>> = [
  { display: 'Include all', value: CriteriaConditionType.IncludeAll },
  { display: 'Include any', value: CriteriaConditionType.IncludeAny },
  { display: 'Exclude', value: CriteriaConditionType.Exclude },
]

/** `turnoverRates` — the threshold in the candidate-turnover row. */
export const TURNOVER_RATE_OPTIONS: ReadonlyArray<{ years: number; display: string }> = [
  { years: 1, display: 'Turnover < 1 year ("job hopper")' },
  { years: 2, display: 'Turnover < 2 years' },
]

/**
 * `turnoverConditionTypeDisplayText`.
 *
 * ⚠️ EXCLUDE COMES FIRST, AND "INCLUDE" IS `INCLUDE_ANY`. Two things a reasonable
 * transcription changes: the ORDER (a list of conditions usually reads include-then-exclude,
 * and this one is the other way round — so the default an unset select shows is Exclude), and
 * the VALUE, since there is no `INCLUDE` member and "Include" maps to `INCLUDE_ANY`.
 */
export const TURNOVER_CONDITION_OPTIONS: ReadonlyArray<Option<CriteriaConditionType>> = [
  { display: 'Exclude', value: CriteriaConditionType.Exclude },
  { display: 'Include', value: CriteriaConditionType.IncludeAny },
]

/** `KeywordSectionCategory` — which part of the resume a keyword condition searches. */
export const KeywordSectionCategory = {
  ResumeFreeText: 0,
  WorkExperience: 1,
  Education: 2,
  CurrentWorkExperience: 3,
} as const
export type KeywordSectionCategory = (typeof KeywordSectionCategory)[keyof typeof KeywordSectionCategory]

/**
 * `keywordSectionCategoryDisplayText`.
 *
 * ⚠️ THE DISPLAY ORDER IS NOT THE ENUM ORDER, and that decides the default. The list runs
 * Current experience (3), Work experience (1), Education (2), Entire resume (0) — so an unset
 * select shows CURRENT_WORK_EXPERIENCE, the enum's LAST member. Listing these in enum order,
 * which is the natural way to write them out, silently changes every new keyword condition
 * from "current experience" to "entire resume".
 */
export const KEYWORD_SECTION_OPTIONS: ReadonlyArray<Option<KeywordSectionCategory>> = [
  { display: 'Current experience', value: KeywordSectionCategory.CurrentWorkExperience },
  { display: 'Work experience', value: KeywordSectionCategory.WorkExperience },
  { display: 'Education', value: KeywordSectionCategory.Education },
  { display: 'Entire resume', value: KeywordSectionCategory.ResumeFreeText },
]

/**
 * `ClientContactKeywordSectionCategory` — the CONTACT builder's Field select, and it is
 * neither a subset of the candidate one nor the same numbers.
 *
 * ⚠️ TWO OPTIONS, NOT FOUR, AND THE ORDINALS COLLIDE. The contact enum is
 * `{ CurrentWorkExperience = 0, WorkExperience = 1 }`
 * (`client-contact-extension-criteria.interface.ng1.ts:82`) where the candidate enum puts
 * `ResumeFreeText` at 0 and `CurrentWorkExperience` at 3 — so rendering the candidate list on
 * the contact form offers Education and Entire resume, which this product has no concept of,
 * AND sends 3 for "Current experience" where the server expects 0. The middle label differs
 * too: "Any experience", not "Work experience".
 */
export const ClientContactKeywordSectionCategory = {
  CurrentWorkExperience: 0,
  WorkExperience: 1,
} as const
export type ClientContactKeywordSectionCategory =
  (typeof ClientContactKeywordSectionCategory)[keyof typeof ClientContactKeywordSectionCategory]

export const CLIENT_CONTACT_KEYWORD_SECTION_OPTIONS: ReadonlyArray<Option<number>> = [
  { display: 'Current experience', value: ClientContactKeywordSectionCategory.CurrentWorkExperience },
  { display: 'Any experience', value: ClientContactKeywordSectionCategory.WorkExperience },
]

/**
 * `search.page.tpl.html:646` — the country select, transcribed verbatim.
 *
 * ⚠️ IT IS NOT ALPHABETICAL AND IT IS NOT A COMPLETE COUNTRY LIST. Three countries are   'United Kingdom',
  'United States',
  'Israel',
 * to the top, above a disabled `──────────` separator that is itself an `<option>` and occupies
 * a row: United Kingdom, United States, Israel. The remaining 71 are alphabetical after a
 * second separator. Sorting the whole thing — which is what an alphabetical helper would do —
 * moves Israel from the third row to the thirty-second and changes the height of nothing, so it
 * shows up only as a pixel diff in the open dropdown.
 *
 * The empty value is "Worldwide", the default, and it is what `country` being unset means on
 * the wire.
 */
export const LOCATION_COUNTRY_PINNED: readonly string[] = ['United Kingdom', 'United States', 'Israel']

export const LOCATION_COUNTRIES: readonly string[] = [
  'Albania',
  'Algeria',
  'Angola',
  'Argentina',
  'Australia',
  'Austria',
  'Belarus',
  'Belgium',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Bulgaria',
  'Canada',
  'China',
  'Colombia',
  'Croatia',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Egypt',
  'El Salvador',
  'Estonia',
  'Finland',
  'France',
  'Georgia',
  'Germany',
  'Greece',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Ireland',
  'Italy',
  'Japan',
  'Kenya',
  'Lithuania',
  'Luxembourg',
  'Malaysia',
  'Mexico',
  'Montenegro',
  'Morocco',
  'Netherlands',
  'New Zealand',
  'Niger',
  'Nigeria',
  'Norway',
  'Panama',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Saudi Arabia',
  'Serbia',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'South Africa',
  'South Korea',
  'Spain',
  'Sweden',
  'Switzerland',
  'Taiwan',
  'Tanzania',
  'Thailand',
  'Tunisia',
  'Turkey',
  'Ukraine',
  'United Arab Emirates',
  'Zambia',
  'Zimbabwe',
]

/**
 * `ctrl.locationDistances` — the "Up to N from <city>" radius.
 *
 * ⚠️ THE MILES ARE NOT A CONVERSION, THEY ARE COPY. 20km reads "10 miles" (12.4 rounded down),
 * 30km reads "20 miles" (18.6 rounded up), and 40km reads "25 miles". Computing them from the
 * kilometres, which is the obvious way to avoid a 15-row table, disagrees with production on
 * eight of the fifteen rows.
 */
export interface LocationDistance {
  distance: number
  display: string
}

export const LOCATION_DISTANCES: readonly LocationDistance[] = [
  { distance: 10, display: '10km (6 miles)' },
  { distance: 20, display: '20km (10 miles)' },
  { distance: 30, display: '30km (20 miles)' },
  { distance: 40, display: '40km (25 miles)' },
  { distance: 50, display: '50km (30 miles)' },
  { distance: 75, display: '75km (45 miles)' },
  { distance: 100, display: '100km (60 miles)' },
  { distance: 150, display: '150km (90 miles)' },
  { distance: 200, display: '200km (125 miles)' },
  { distance: 300, display: '300km (185 miles)' },
  { distance: 400, display: '400km (250 miles)' },
  { distance: 500, display: '500km (310 miles)' },
  { distance: 600, display: '600km (370 miles)' },
  { distance: 700, display: '700km (435 miles)' },
  { distance: 800, display: '800km (500 miles)' },
  { distance: 900, display: '900km (560 miles)' },
  { distance: 1000, display: '1000km (620 miles)' },
]
